import React, { useState, useEffect } from "react";
import { CloseButton, OutlineButton, PrimaryButton } from "./Buttons";
import { Loader2 } from "lucide-react";
import axiosInstance from "../services/api/axios";
import { useUser } from "../context/UserContext";
import { fetchPropertyConfigurations } from "../services/projects";

function RescheduleVisit({ open, onClose, onSubmit, visit, configurations }) {

  const [formData, setFormData] = useState({
    propertyConfig: visit?.propertyConfig?.propertyConfigId || "",
    preferredDate: "",
    preferredTime: "",
    buildingId: visit?.building?.buildingId || "", // Use building from the visit object
  });
  const { userInfo } = useUser();
  const [buildings, setBuildings] = useState([]);
  const [configOptions, setConfigOptions] = useState([]);
  const [agentId, setAgentId] = useState(visit?.agentId || '');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        let params = {
          customerId:
            userInfo.loginIdentifierDetails.customer?.customerId ??
            userInfo.loginIdentifierDetails.customerId,
        };

        if (userInfo?.persona === "CHANNEL_PARTNER") {
          params.agentId = userInfo.loginIdentifierDetails.customerId;
          delete params.customerId;
        }

        const response = await axiosInstance.get(`/projects/buildings`, {
          params: params,
        });

        const buildingData = response.map((building) => ({
          buildingId: building.buildingId,
          buildingName: building.buildingName,
        }));
        setBuildings(buildingData);
      } catch (error) {
        console.error("Error fetching buildings:", error);
      }
    };

    fetchBuildings();
  }, [userInfo]);

  useEffect(() => {
    const fetchConfigs = async () => {
      if (formData.buildingId) {
        setIsLoading(true);
        try {
       

          const response = await fetchPropertyConfigurations({
            customerId: userInfo?.loginIdentifierDetails?.customer?.customerId,
            buildingId: formData.buildingId,
          });

          setConfigOptions(response);
        } catch (error) {
          console.error("Error fetching configurations:", error);
          setConfigOptions([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setConfigOptions([]);
      }
    };
    fetchConfigs();
  }, [formData.buildingId, userInfo]); 


  useEffect(() => {
    
    if (visit) {
      setFormData({
        propertyConfig: visit?.propertyConfig?.propertyConfigId || "",
        preferredDate: visit?.visitSchedule?.split('T')[0] || "", // Extract date part
        preferredTime: visit?.visitSchedule?.split('T')[1]?.substring(0, 5) || "", // Extract time part (HH:mm)
        buildingId: visit?.building?.buildingId || "",
        agentId: agentId,

      });
    }
  }, [visit]);  // Only run when the `visit` prop changes


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBuildingChange = (e) => {
    setFormData({ ...formData, buildingId: e.target.value, propertyConfig: "" });
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("Error submitting:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg p-6 relative">
      <CloseButton className="absolute right-4 top-4" onClick={onClose} />
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Reschedule Visit</h3>

        <div className="mb-4">
          <label htmlFor="buildingId" className="block text-sm font-medium text-gray-700">
            Select Building:
          </label>
          <select
            id="buildingId"
            name="buildingId"
            className="mt-1 block border w-full pl-3 pr-10 py-2 text-base  focus:outline-none focus:ring-2 focus:ring-blue-400 sm:text-sm rounded-md"
            value={formData.buildingId}
            onChange={handleBuildingChange}
            required
            disabled={isLoading}
          >
            <option value="" disabled>
              Select a Building
            </option>
            {buildings.map((building) => (
              <option key={building.buildingId} value={building.buildingId}>
                {building.buildingName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="propertyConfig" className="block text-sm font-medium text-gray-700 mb-1">
            Select Configuration
          </label>
          <select
            id="propertyConfig"
            name="propertyConfig"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={formData.propertyConfig}
            onChange={handleChange}
            disabled={isLoading || !formData.buildingId}
          >
            <option value="" disabled>
              Choose a configuration
            </option>
            {configOptions.map((config) => (
              <option key={config.propertyConfigId} value={config.propertyConfigId}>
                {config.configName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Date
          </label>
          <input
            type="date"
            id="preferredDate"
            name="preferredDate"
            value={formData.preferredDate}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            disabled={isLoading}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Time
          </label>
          <input
            type="time"
            id="preferredTime"
            name="preferredTime"
            value={formData.preferredTime}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            disabled={isLoading}
          />
        </div>

        <div className="flex justify-end gap-3">
          <PrimaryButton onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : "Submit"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default RescheduleVisit;